/** @jsxImportSource @emotion/react */

import type { Theme } from '@emotion/react';
import type { ChangeEventHandler } from 'react';

type CheckboxDefault = {
  cssProps?: object;
  checked?: boolean;
  hidden?: boolean;
  name?: string;
  onChange?: ChangeEventHandler<HTMLInputElement>;
  value?: string;
  inputRef?: React.Ref<HTMLInputElement>;
};

type CheckboxProps = CheckboxDefault & {
  children: React.ReactNode;
};

const size = '22px';

const containerClass = () =>
  ({
    cursor: 'default',
    fontFamily: 'inherit',
    fontSize: 'inherit',
    fontWeight: 'inherit'
  }) as const;

const countClassFn = () => ({
  color: 'hsl(0, 0%, 10%)',
  fontSize: '0.8em',
  fontWeight: 'normal',
  opacity: '0.8'
});

const inputClass = () =>
  ({
    position: 'absolute',
    opacity: 0,
    ':focus': {
      boxShadow: 'none'
    }
  }) as const;

const labelClass = (t: Theme) =>
  ({
    display: 'block',
    lineHeight: size,
    paddingLeft: `calc(${size} + 8px)`,
    position: 'relative',

    '::before': {
      backgroundColor: 'white',
      border: `1px solid ${t.color.grey.g40}`,
      borderRadius: '3px',
      content: '""',
      display: 'block',
      height: size,
      fontSize: '16px',
      fontWeight: 'normal',
      left: 0,
      position: 'absolute',
      textAlign: 'center',
      top: 0,
      transition: 'background-color 200ms ease-out',
      width: size
    },

    '.checkboxContainer:hover input:not(:checked) ~ &': {
      color: t.color.primary
    },

    'input:checked ~ &': {
      fontWeight: 'bold',

      '::before': {
        backgroundColor: t.color.primary,
        color: 'white',
        content: '"✓"'
      }
    }
  }) as const;

const Checkbox: React.FC<CheckboxProps> = ({
  cssProps = {},
  checked,
  children,
  name,
  onChange,
  value,
  inputRef
}) => {
  return (
    <label css={{ ...containerClass, ...cssProps }} className="checkboxContainer">
      <input css={inputClass} type="checkbox" ref={inputRef} {...{ checked, name, onChange, value }} />
      <span css={labelClass}>{children}</span>
    </label>
  );
};

export default Checkbox;

type CheckboxWithCounterProps = CheckboxDefault & {
  option: {
    label: string | React.ReactNode;
    count: number;
  };
};

const CheckboxWithCounter: React.FC<CheckboxWithCounterProps> = ({ option, ...props }) => (
  <Checkbox {...props} {...option}>
    <span>{option.label}</span>&nbsp;
    <span css={countClassFn}>({option.count})</span>
  </Checkbox>
);

export { containerClass, labelClass, inputClass, countClassFn };
export { CheckboxWithCounter };
